import { useEffect, useState, useCallback, useMemo } from "react";
import {
  chListCentros, chListInspeccionesEmpresa, chGetSeguimiento,
  chActualizarSeguimiento, chCompararInspecciones,
} from "@/lib/prevelis";
import { formatFecha } from "@/lib/format";
import { toast } from "sonner";
import { Loader2, ClipboardList, AlertTriangle, CheckCircle2, GitCompareArrows, ArrowRight } from "lucide-react";

const ESTADOS = [
  { codigo: "pendiente", label: "Pendiente", cls: "bg-slate-100 text-slate-700 border-slate-300" },
  { codigo: "en_curso", label: "En curso", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  { codigo: "resuelta", label: "Resuelta", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
];
const estadoCls = (c) => (ESTADOS.find((e) => e.codigo === c) || ESTADOS[0]).cls;
const prioCls = (p) => {
  const s = (p || "").toLowerCase();
  if (s.includes("3") || s.includes("élev") || s.includes("elev") || s.includes("alta")) return "bg-red-100 text-red-700";
  if (s.includes("1") || s.includes("faible") || s.includes("baja")) return "bg-emerald-100 text-emerald-700";
  return "bg-amber-100 text-amber-800";
};
const inp = "h-9 px-2 rounded-lg border border-slate-300 text-sm outline-none focus:border-blue-500 bg-white";

function StatCard({ icon: Icon, label, value, tone, testId }) {
  const tones = { blue: "text-blue-700 bg-blue-50", red: "text-red-700 bg-red-50", emerald: "text-emerald-700 bg-emerald-50", slate: "text-slate-700 bg-slate-100" };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3" data-testid={testId}>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${tones[tone]}`}><Icon className="w-5 h-5" /></div>
      <div className="min-w-0">
        <div className="text-2xl font-heading font-semibold leading-none">{value}</div>
        <div className="text-xs text-slate-500 mt-1">{label}</div>
      </div>
    </div>
  );
}

export default function SuizaSeguimiento({ empresaId }) {
  const [centros, setCentros] = useState([]);
  const [insps, setInsps] = useState([]);
  const [filtroCentro, setFiltroCentro] = useState("");
  const [data, setData] = useState({ items: [], stats: {}, num_inspecciones: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    chGetSeguimiento(empresaId, filtroCentro).then(setData).catch(() => {}).finally(() => setLoading(false));
  }, [empresaId, filtroCentro]);

  useEffect(() => {
    chListCentros(empresaId).then(setCentros).catch(() => {});
    chListInspeccionesEmpresa(empresaId).then(setInsps).catch(() => {});
  }, [empresaId]);
  useEffect(() => { load(); }, [load]);

  const patchItem = (it, patch) => {
    setData((d) => ({ ...d, items: d.items.map((x) => (x.inspeccion_id === it.inspeccion_id && x.idx === it.idx ? { ...x, ...patch } : x)) }));
  };
  const saveItem = (it, patch) => {
    patchItem(it, patch);
    chActualizarSeguimiento(it.inspeccion_id, { idx: it.idx, ...patch })
      .then(() => { if ("estado" in patch) load(); })
      .catch(() => toast.error("No se pudo guardar el seguimiento"));
  };

  const stats = data.stats || {};
  const abiertas = (stats.pendiente || 0) + (stats.en_curso || 0);

  return (
    <div className="space-y-5" data-testid="ch-seguimiento-panel">
      {/* Dashboard */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={ClipboardList} label="Inspecciones" value={data.num_inspecciones || 0} tone="blue" testId="ch-seg-stat-insp" />
        <StatCard icon={AlertTriangle} label="Deficiencias abiertas" value={abiertas} tone="red" testId="ch-seg-stat-abiertas" />
        <StatCard icon={CheckCircle2} label="Resueltas" value={stats.resuelta || 0} tone="emerald" testId="ch-seg-stat-resueltas" />
        <StatCard icon={CheckCircle2} label="% resueltas" value={`${stats.pct_resuelta || 0}%`} tone="slate" testId="ch-seg-stat-pct" />
      </div>

      {/* Seguimiento de medidas */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
          <div>
            <h3 className="font-heading font-semibold text-lg">Seguimiento de medidas</h3>
            <p className="text-sm text-slate-500">Estado de las medidas correctoras de todas las inspecciones de la empresa.</p>
          </div>
          <select value={filtroCentro} onChange={(e) => setFiltroCentro(e.target.value)} className={inp + " h-10"} data-testid="ch-seg-filtro-centro">
            <option value="">Todos los centros</option>
            {centros.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="py-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
        ) : (data.items || []).length === 0 ? (
          <div className="py-8 text-center text-slate-500 text-sm border border-dashed border-slate-300 rounded-lg">
            No hay medidas registradas todavía. Analiza y valida inspecciones para poblar el seguimiento.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="ch-seg-tabla">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-3 font-medium">Peligro / Medida</th>
                  <th className="py-2 px-2 font-medium">Centro</th>
                  <th className="py-2 px-2 font-medium">Prioridad</th>
                  <th className="py-2 px-2 font-medium">Estado</th>
                  <th className="py-2 px-2 font-medium">Responsable</th>
                  <th className="py-2 px-2 font-medium">Plazo</th>
                </tr>
              </thead>
              <tbody>
                {(data.items || []).map((it) => (
                  <tr key={`${it.inspeccion_id}-${it.idx}`} className="border-b border-slate-100 align-top" data-testid={`ch-seg-row-${it.inspeccion_id}-${it.idx}`}>
                    <td className="py-2 pr-3 max-w-[380px]">
                      <div className="font-medium text-slate-800">{it.peligro || "—"}</div>
                      {it.medidas_necesarias && <div className="text-xs text-slate-500 mt-0.5">{it.medidas_necesarias}</div>}
                    </td>
                    <td className="py-2 px-2 text-slate-600 whitespace-nowrap">{it.centro_nombre || "—"}</td>
                    <td className="py-2 px-2">{it.prioridad ? <span className={`text-xs px-2 py-0.5 rounded-full ${prioCls(it.prioridad)}`}>{it.prioridad}</span> : "—"}</td>
                    <td className="py-2 px-2">
                      <select value={it.estado} onChange={(e) => saveItem(it, { estado: e.target.value })}
                        className={`h-8 px-2 rounded-lg border text-xs font-medium outline-none ${estadoCls(it.estado)}`} data-testid={`ch-seg-estado-${it.inspeccion_id}-${it.idx}`}>
                        {ESTADOS.map((e) => <option key={e.codigo} value={e.codigo}>{e.label}</option>)}
                      </select>
                      {it.estado === "resuelta" && it.fecha_resuelta && <div className="text-[10px] text-emerald-600 mt-1">✓ {formatFecha(it.fecha_resuelta)}</div>}
                    </td>
                    <td className="py-2 px-2">
                      <input defaultValue={it.responsable} onBlur={(e) => e.target.value !== it.responsable && saveItem(it, { responsable: e.target.value })}
                        placeholder="—" className={inp + " w-32"} data-testid={`ch-seg-resp-${it.inspeccion_id}-${it.idx}`} />
                    </td>
                    <td className="py-2 px-2">
                      <input defaultValue={it.plazo} onBlur={(e) => e.target.value !== it.plazo && saveItem(it, { plazo: e.target.value })}
                        placeholder="—" className={inp + " w-28"} data-testid={`ch-seg-plazo-${it.inspeccion_id}-${it.idx}`} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Comparador centros={centros} insps={insps} />
    </div>
  );
}

function Comparador({ centros, insps }) {
  const [centroId, setCentroId] = useState("");
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  const opciones = useMemo(() => insps.filter((i) => !centroId || i.centro_id === centroId), [insps, centroId]);

  const comparar = () => {
    if (!a || !b || a === b) { toast.error("Selecciona dos inspecciones distintas"); return; }
    setBusy(true);
    chCompararInspecciones(centroId, a, b).then(setRes).catch((e) => toast.error(e?.response?.data?.detail || "Error al comparar")).finally(() => setBusy(false));
  };

  const Col = ({ title, tone, items, empty, render, testId }) => (
    <div className="flex-1 min-w-[220px]" data-testid={testId}>
      <div className={`text-sm font-semibold px-3 py-2 rounded-t-lg ${tone}`}>{title} ({items.length})</div>
      <div className="border border-t-0 border-slate-200 rounded-b-lg divide-y divide-slate-100 max-h-[420px] overflow-y-auto">
        {items.length === 0 ? <div className="p-3 text-xs text-slate-400">{empty}</div> : items.map((x, i) => <div key={i} className="p-3 text-sm">{render(x)}</div>)}
      </div>
    </div>
  );

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="ch-comparador">
      <div className="flex items-center gap-2 mb-1">
        <GitCompareArrows className="w-5 h-5 text-blue-700" />
        <h3 className="font-heading font-semibold text-lg">Comparar inspecciones</h3>
      </div>
      <p className="text-sm text-slate-500 mb-4">Empareja automáticamente los peligros por similitud para ver qué se ha resuelto, qué persiste y qué es nuevo.</p>
      <div className="flex flex-wrap items-end gap-2 mb-4">
        <select value={centroId} onChange={(e) => { setCentroId(e.target.value); setA(""); setB(""); setRes(null); }} className={inp + " h-10"} data-testid="ch-comp-centro">
          <option value="">Elige centro…</option>
          {centros.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
        </select>
        <select value={a} onChange={(e) => setA(e.target.value)} disabled={!centroId} className={inp + " h-10 disabled:opacity-50"} data-testid="ch-comp-a">
          <option value="">Inspección anterior…</option>
          {opciones.map((i) => <option key={i.id} value={i.id}>{i.titulo} · {formatFecha(i.fecha_inspeccion) || "s/f"}</option>)}
        </select>
        <ArrowRight className="w-4 h-4 text-slate-400 mb-2.5" />
        <select value={b} onChange={(e) => setB(e.target.value)} disabled={!centroId} className={inp + " h-10 disabled:opacity-50"} data-testid="ch-comp-b">
          <option value="">Inspección actual…</option>
          {opciones.map((i) => <option key={i.id} value={i.id}>{i.titulo} · {formatFecha(i.fecha_inspeccion) || "s/f"}</option>)}
        </select>
        <button onClick={comparar} disabled={busy || !a || !b} className="h-10 px-4 rounded-lg bg-blue-700 text-white text-sm font-medium hover:bg-blue-800 disabled:opacity-50 inline-flex items-center gap-2" data-testid="ch-comp-run">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <GitCompareArrows className="w-4 h-4" />} Comparar
        </button>
      </div>

      {res && (
        <div className="flex flex-wrap gap-3" data-testid="ch-comp-resultado">
          <Col title="Resueltas" tone="bg-emerald-600 text-white" items={res.resueltas} empty="Ninguna resuelta." testId="ch-comp-resueltas"
            render={(x) => <div><div className="text-slate-800 line-through decoration-emerald-500/60">{x.anterior || x.actual}</div></div>} />
          <Col title="Persistentes" tone="bg-amber-500 text-white" items={res.persistentes} empty="Ninguna persistente." testId="ch-comp-persistentes"
            render={(x) => <div><div className="font-medium text-slate-800">{x.actual}</div>{x.medidas_necesarias && <div className="text-xs text-slate-500 mt-0.5">{x.medidas_necesarias}</div>}</div>} />
          <Col title="Nuevas" tone="bg-red-600 text-white" items={res.nuevas} empty="Ninguna nueva." testId="ch-comp-nuevas"
            render={(x) => <div><div className="font-medium text-slate-800">{x.actual}</div>{x.medidas_necesarias && <div className="text-xs text-slate-500 mt-0.5">{x.medidas_necesarias}</div>}</div>} />
        </div>
      )}
    </div>
  );
}
